// Publisher-team flavor of the generic PeopleSearchMolecule picker: the same global
// people-search (identity.discover gated) used by the channel-audience invite, plus a
// role dropdown (trailing control) and team-specific labels. A publisher is a
// metasphere-scoped entity (never created inside an organization), so teammate search
// is ALWAYS the global people search — there is no org-scoped mode here.
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { PublisherMember } from 'types/publisher';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import PeopleSearchMolecule from '../people-search/PeopleSearchMolecule';
import type { PeopleSearchResult } from '../../../api/marketplaceApi';

export interface AddTeammateMoleculeProps {
  members: PublisherMember[];
  canTransfer: boolean;
  onAdd: (identityId: string, role: 'manager' | 'creator') => Promise<boolean>;
  isMutating: boolean;
  /** Optional callback fired after each debounced search settles (for analytics). */
  onTeammateSearched?: (resultCount: number) => void;
  className?: string;
}

const NS = 'marketplace.publishers.manage.team.invite';

const AddTeammateMolecule: React.FC<AddTeammateMoleculeProps> = ({
  members,
  canTransfer,
  onAdd,
  isMutating,
  onTeammateSearched,
  className = '',
}) => {
  const { t } = useTranslation();
  const [role, setRole] = useState<'manager' | 'creator'>('creator');

  const excludeIdentityIds = useMemo(() => members.map((m) => m.identityId), [members]);
  const excludeEmails = useMemo(
    () => members.map((m) => (m.email ?? '').toLowerCase()).filter(Boolean),
    [members]
  );

  const roleOptions = useMemo(
    () => [
      ...(canTransfer ? [{ label: t(`${NS}.roleManager`), value: 'manager' }] : []),
      { label: t(`${NS}.roleCreator`), value: 'creator' },
    ],
    [canTransfer, t]
  );

  const labels = useMemo(
    () => ({
      searchPlaceholder: t(`${NS}.searchPlaceholder`),
      addButton: t(`${NS}.addButton`),
      alreadyAdded: t(`${NS}.alreadyOnTeam`),
      noMatch: (query: string) => t(`${NS}.noMatch`, { query }),
    }),
    [t]
  );

  const handleAdd = useCallback(
    (person: PeopleSearchResult) => onAdd(person.identityId, role),
    [onAdd, role]
  );

  return (
    <PeopleSearchMolecule
      className={['add-teammate-molecule', className].filter(Boolean).join(' ')}
      onSelect={handleAdd}
      isMutating={isMutating}
      labels={labels}
      excludeIdentityIds={excludeIdentityIds}
      excludeEmails={excludeEmails}
      onSearched={onTeammateSearched}
      trailing={
        <DropdownAtom
          className="add-teammate-molecule__role-select"
          dropdownSize="small"
          value={role}
          options={roleOptions}
          onChange={(e) => setRole(e.value as 'manager' | 'creator')}
          disabled={isMutating}
          ariaLabel={t(`${NS}.roleLabel`)}
        />
      }
    />
  );
};

export default AddTeammateMolecule;
